import { createBrowserHistory } from "history";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import {
  AdminContext,
  AdminUI,
  Resource,
  useNotify,
  usePermissions,
} from "react-admin";
import { Provider, useDispatch } from "react-redux";
import { Route } from "react-router";
import { backToPortal } from "../acs/links";
import { ConfigContext } from "../index";
import Error from "../reusable-components/Error";
import layout, {
  ICustomLayoutProps,
} from "../reusable-components/layout/Layout";
import Logout from "../reusable-components/layout/Logout";
import { ContactTranspower } from "../reusable-components/page/ContactTranspower";
import { OrgsResourceInternal } from "../reusable-components/resource/orgs";
import { TPMaterialTheme } from "../reusable-components/tp-materialui";
import { AppProvider } from "../shared/context/AppContext";
import { LoginResultContext } from "../shared/context/LoginResultContext";
import { OCPDataProvider } from "../shared/dataProvider/OCPDataProvider";
import { i18nProvider } from "../shared/i18n/i18nProvider";
import { changeUserContext } from "../shared/middlewares/actions";
import { createUserContext } from "../shared/middlewares/changeUserContextReducer";
import createAdminStore from "../shared/middlewares/createAdminStore";
import useOCPAuthProvider from "../shared/useOCPAuthProvider";
import useUserContext from "../shared/useUserContext";
import { IUserContext } from "../types";
import Dashboard from "./Dashboard";
import { getPortalLinks } from "./links";

const history = createBrowserHistory({ basename: "/" });

const getlayout = (config) => (props) => {
  return layout({
    links: getPortalLinks(config),
    hideResources: false,
    hideDashboard: true,
    ...props,
    theme: TPMaterialTheme,
    title: "" /* Suppress tooltips */,
  } as ICustomLayoutProps);
};

const contactTranspower = (props) => {
  return (
    <>
      <ContactTranspower
        links={[backToPortal]}
        appname={"portal.title.contactTranspower"}
        email={"portal.contact"}
        subject={"portal.subject"}
        {...props}
      />
    </>
  );
};

const customRoutes = [
  <Route exact path="/contact" component={contactTranspower} noLayout />,
];

function AsyncResources() {
  const [resources, setResources] = useState<any[]>([]);
  const dispatch = useDispatch();
  const notify = useNotify();
  const loginResult = useContext(LoginResultContext);
  const userContext = useUserContext();
  const config = React.useContext(ConfigContext);

  const { loaded, permissions } = usePermissions();

  /*
   *  This effect will setup user menu and resources based on the permissions that are loaded
   */
  useEffect(() => {
    if (loaded && !userContext.initialised) {
      const userContext: IUserContext = createUserContext(permissions);

      if (userContext.isInternal) {
        setResources([
          {
            name: "orgs",
            key: "orgs",
            options: {
              label: "portal.title.participant",
              menuSectionHeader: "portal.menu.portalAdmin",
            },
            ...OrgsResourceInternal,
          },
        ]);
      } else {
        /* Create a dummy resource, otherwise RA won't load up its router */
        setResources([
          {
            name: "orgs",
            key: "orgs",
            intent: "registration",
          },
        ]);
      }
      dispatch(changeUserContext(createUserContext(permissions)));
    }
  }, [loaded, permissions, userContext.initialised, dispatch]);

  useEffect(() => {
    if (loginResult.authRedirectErrorMessage) {
      notify(loginResult.authRedirectErrorMessage);
    }
  }, [loginResult, notify]);

  return (
    <AdminUI
      layout={getlayout(config)}
      customTheme={TPMaterialTheme}
      customRoutes={customRoutes}
      dashboard={Dashboard}
      // @ts-ignore
      logout={Logout}
      disableTelemetry
    >
      {resources.map((resource) => (
        <Resource name={resource.name} key={resource.key} {...resource} />
      ))}
    </AdminUI>
  );
}

/* Bit of a hack... */
const resourceToPath = (resource) => {
  return `identity/${resource}`;
};

const App = () => {
  const config = useContext(ConfigContext);
  const authProvider = useOCPAuthProvider();
  const dataProvider = OCPDataProvider(
    config.REACT_APP_PORTAL_SERVER_URL || "",
    resourceToPath
  );

  return (
    <AppProvider>
      <Provider
        store={createAdminStore({
          authProvider,
          dataProvider,
          history,
        })}
      >
        <AdminContext
          authProvider={authProvider}
          dataProvider={dataProvider}
          i18nProvider={i18nProvider}
          history={history}
          // @ts-ignore
          title="portal.title.customerPortal"
          error={Error}
          disableTelemetry
        >
          <AsyncResources />
        </AdminContext>
      </Provider>
    </AppProvider>
  );
};

export default App;
